// Role detection and permission helpers

/**
 * Check if user has Risk Manager role
 * @param {Array<string>} userRoles - Array of role strings from JWT claims
 * @returns {boolean}
 */
export const isRiskManager = (userRoles = []) => {
  return userRoles.some(role =>
    role.toLowerCase().includes('risk manager') ||
    role.toLowerCase().includes('riskmanager') ||
    role.toLowerCase() === 'manager'
  );
};

/**
 * Check if user has Team Member role (tech team users who run assessments)
 * Also matches "Data Scientist" for backward compat with legacy Huginn roles.
 * @param {Array<string>} userRoles - Array of role strings from JWT claims
 * @returns {boolean}
 */
export const isTeamMember = (userRoles = []) => {
  return userRoles.some(role => {
    const r = role.toLowerCase();
    return r.includes('team member') || r.includes('teammember')
      || r.includes('data scientist') || r.includes('datascientist');
  });
};

/**
 * Normalise an Auth0 role name. Mirrors netlify/functions/authutils/roles.js so the same
 * spelling is accepted on both sides: "Platform Admin", "platform-admin" and "PLATFORM_ADMIN"
 * all reduce to 'platform_admin'.
 */
const norm = (r) => String(r || '').trim().toLowerCase().replace(/[\s-]+/g, '_');

/**
 * Check if user holds the Reviewer role.
 *
 * Reviewer is RiskManager's `risk_lead`: it carries sign-off authority for tier-1 assessments
 * (docs/BRIEF.md §6, sign-off gates). It therefore has to count as an authorised role, or a
 * reviewer authenticates successfully and is then refused entry.
 */
export const isReviewer = (userRoles = []) => userRoles.some((r) => norm(r) === 'reviewer');

/**
 * First line: the person accountable for an agent, whose account the mandate is.
 *
 * They never sign — signing is second line's act, and the owner's Workbench has no Sign button
 * anywhere (docs/BRIEF.md §6). This is used to pick the navigation, so being wrong in the
 * permissive direction would show an owner the whole register; the caller therefore treats
 * "owner AND something else" as second line, not first.
 *
 * Auth0 role name: `Agent Owner`.
 */
export const isAgentOwner = (userRoles = []) => userRoles.some((r) => norm(r) === 'agent_owner');

/** Platform super-admin. Mirrors hasPlatformAdmin in netlify/functions/authutils/roles.js. */
export const isPlatformAdminRole = (userRoles = []) =>
  userRoles.some((r) => norm(r) === 'platform_admin');

/**
 * Check if user has any explicitly granted role.
 *
 * Guards the first of App.js's two entry gates: `userRoles.length > 0 && !hasAuthorizedRole`
 * shows the "not authorized" screen. The second gate (no org and not a platform admin) shows
 * "Awaiting access".
 *
 * Reviewer, platform_admin and agent_owner are included deliberately. Without them:
 *   * a user granted ONLY `platform_admin` — the intended way to bootstrap a fresh deployment,
 *     since it is what lets you self-assign an org at /agency/admin — has a non-empty role
 *     array containing nothing "authorised", so gate 1 locks them out before gate 2 can let
 *     them through. First login on a new environment is impossible.
 *   * a reviewer-only user is refused even though Reviewer is `risk_lead`.
 *   * an owner-only user — the entire first-line half of the product — is refused at the
 *     door: every owner screen exists behind a gate that never admitted the owner's role.
 *     Found live on the demo tenant, 2026-08-10.
 *
 * @param {Array<string>} userRoles - Array of role strings from JWT claims
 * @returns {boolean}
 */
export const hasAuthorizedRole = (userRoles = []) => {
  return isRiskManager(userRoles)
    || isTeamMember(userRoles)
    || isReviewer(userRoles)
    || isAgentOwner(userRoles)
    || isPlatformAdminRole(userRoles);
};

/**
 * Check if user is a regular user (non-manager)
 * @param {Array<string>} userRoles - Array of role strings from JWT claims
 * @returns {boolean}
 */
export const isRegularUser = (userRoles = []) => {
  return !isRiskManager(userRoles);
};

/**
 * Get display label for role
 * @param {Array<string>} userRoles - Array of role strings from JWT claims
 * @returns {string}
 */
export const getRoleLabel = (userRoles = []) => {
  if (isRiskManager(userRoles)) {
    return 'Risk Manager';
  }
  if (isTeamMember(userRoles)) {
    return 'Team Member';
  }
  return userRoles[0] || 'User';
};

/**
 * Check if user has permission to access a route
 * @param {string} path - Route path
 * @param {Array<string>} userRoles - Array of role strings from JWT claims
 * @returns {boolean}
 */
export const canAccessRoute = (path, userRoles = []) => {
  const managerOnlyRoutes = [
    '/risk-themes',
    '/risk-form-manager',
    '/file-upload',
    '/universal-uploader',
    '/risk-catalogues',
    '/risk-policies',
  ];

  // Risk Managers can access everything
  if (isRiskManager(userRoles)) {
    return true;
  }

  // Regular users cannot access manager-only routes
  return !managerOnlyRoutes.some(route => path.startsWith(route));
};
